import {
  BoltIcon,
  ChartBarIcon,
  ChatBubbleLeftIcon,
  RocketLaunchIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import DataFlow from '../icons/dataflow-04.svg?react';
import { UseCasePath } from './useCasePaths';

const useCaseTypes = ['Business applications'] as const;

export type UseCaseType = (typeof useCaseTypes)[number];

const ICON_MAP = {
  dataflow: DataFlow,
  bolt: BoltIcon,
  'chart-bar': ChartBarIcon,
  'chat-bubble-left': ChatBubbleLeftIcon,
  rocket: RocketLaunchIcon,
  share: ShareIcon,
} as const;

type IconKeyType = keyof typeof ICON_MAP;

type CtaType = 'contact' | 'register';

interface FaqItem {
  q: string;
  a: string;
}

interface UseCaseRawInterface {
  headline: string;
  heroHeadline: string;
  heroHighlight: string;
  secondaryHeadline: string;
  cardImage: string;
  screenshot: string;
  title: string;
  metaDescription: string;
  blurb: string;
  path: UseCasePath;
  type: UseCaseType;
  useCaseName: string;
  headTerm: string;
  ctaType?: CtaType;
  ctaTitle: string;
  ctaBody: string;
  heroKicker?: string;
  capabilities?: string[];
  faqs?: FaqItem[];
  problemBlocks: Array<{
    title: string;
    description: string;
    image: string;
    mask?: boolean;
  }>;
  stepBlocks?: {
    headline: string;
    blocks: Array<{
      icon: IconKeyType;
      title: string;
      description: string;
      time?: string;
      comingSoon?: boolean;
    }>;
  };
}

export interface UseCaseInterface
  extends Omit<UseCaseRawInterface, 'path' | 'stepBlocks'> {
  path: UseCasePath;
  stepBlocks?: {
    headline: string;
    blocks: Array<{
      icon: HeroIconType;
      title: string;
      description: string;
      time?: string;
      comingSoon?: boolean;
    }>;
  };
}

function resolve(raw: UseCaseRawInterface): UseCaseInterface {
  const { stepBlocks, ...rest } = raw;

  return {
    ...rest,
    path: raw.path,
    ...(stepBlocks
      ? {
          stepBlocks: {
            ...stepBlocks,
            blocks: stepBlocks.blocks.map((b) => ({
              ...b,
              icon: ICON_MAP[b.icon],
            })),
          },
        }
      : {}),
  };
}

const modules = import.meta.glob<{ default: UseCaseRawInterface }>(
  './use-cases/*.json',
  {
    eager: true,
  }
);

const useCases: UseCaseInterface[] = Object.values(modules).map((m) =>
  resolve(m.default)
);

export const useCaseData = {
  useCases,
  useCaseTypes,
};
